import { useState } from 'react'
import { toast } from 'sonner'

import { ownerSaveErrorMessage } from '@/components/Chat/useOwnerInfoForm'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { formatDate, formatPhone } from '@/lib/formatters'
import { fillApplicationError, type OwnerSuggestion, promoteOwner } from '@/models/fillApplication'
import { type BusinessOwner, updateBusinessOwner } from '@/models/user'
import { suggestionFills, suggestionName, suggestsOtherEmail } from './ownerSuggestion'

interface OwnerSuggestionRowProps {
  orgId: string
  owner: BusinessOwner | null | undefined
  suggestion: OwnerSuggestion
  onChanged: () => void
}

export function OwnerSuggestionRow({ orgId, owner, suggestion, onChanged }: OwnerSuggestionRowProps) {
  const [busy, setBusy] = useState(false)
  const name = suggestionName(suggestion)
  const ownerName = [owner?.firstName, owner?.lastName].filter(Boolean).join(' ') || 'The current owner'
  const ownerFirstName = owner?.firstName || 'the owner'

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true)
    try {
      await action()
      toast.success(success)
      onChanged()
    } catch (error) {
      toast.error(fillApplicationError(error)?.detail ?? ownerSaveErrorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  if (!suggestion.samePerson) {
    return (
      <li className="flex flex-col items-start gap-2 rounded-md border p-3 text-sm">
        <span>
          The documents name {name}
          {suggestion.email && <> ({suggestion.email})</>} as the business owner.
        </span>
        <ConfirmationDialog
          title={`Make ${name} the business owner?`}
          description={`Cai adds ${name} as a new business owner and fills the application in for them. ${ownerName} keeps their account and role.`}
          confirmLabel="Make them the owner"
          onConfirm={() => run(() => promoteOwner(orgId, suggestion.id), `${name} is now the business owner`)}
        >
          <Button type="button" variant="outline" size="sm" disabled={busy}>
            Make {name} the business owner
          </Button>
        </ConfirmationDialog>
      </li>
    )
  }

  const fills = suggestionFills(owner, suggestion)
  const details = [
    name,
    suggestion.phone ? formatPhone(suggestion.phone) : null,
    suggestion.dob ? `born ${formatDate(suggestion.dob)}` : null,
  ].filter(Boolean)

  return (
    <li className="flex flex-col items-start gap-2 rounded-md border p-3 text-sm">
      <span>From the documents: {details.join(', ')}.</span>
      {Object.keys(fills).length > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => void run(() => updateBusinessOwner(orgId, fills), 'Filled in from the documents')}
        >
          Use these
        </Button>
      )}
      {suggestsOtherEmail(owner, suggestion) && suggestion.email && (
        <>
          <span className="text-ink-500">The documents use {suggestion.email}.</span>
          <ConfirmationDialog
            title={`Change the email ${ownerFirstName} signs in with?`}
            description={`${suggestion.email} becomes the address Cai sends sign-in links to. Links already sent stop working.`}
            confirmLabel="Change the email"
            onConfirm={() =>
              run(() => updateBusinessOwner(orgId, { email: suggestion.email! }), 'Sign-in email changed')
            }
          >
            <Button type="button" variant="outline" size="sm" disabled={busy}>
              Change the email {ownerFirstName} signs in with
            </Button>
          </ConfirmationDialog>
        </>
      )}
    </li>
  )
}
